import { useState, useEffect } from "react";
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import classNames from "../../utility/classNames";

const getToastStyles = (type) => {
  switch (type) {
    case "success":
      return {
        containerClass: "bg-white border-signal-green-100 border-l-signal-green-500",
        iconClass: "text-signal-green-500",
        titleClass: "text-ink-900",
        messageClass: "text-ink-600",
        icon: CheckCircleIcon,
        focusRing: "focus:ring-signal-green-500/30",
      };
    case "error":
      return {
        containerClass: "bg-white border-signal-red-100 border-l-signal-red-500",
        iconClass: "text-signal-red-500",
        titleClass: "text-ink-900",
        messageClass: "text-ink-600",
        icon: XCircleIcon,
        focusRing: "focus:ring-signal-red-500/30",
      };
    case "warning":
      return {
        containerClass: "bg-white border-signal-amber-100 border-l-signal-amber-500",
        iconClass: "text-signal-amber-500",
        titleClass: "text-ink-900",
        messageClass: "text-ink-600",
        icon: ExclamationTriangleIcon,
        focusRing: "focus:ring-signal-amber-500/30",
      };
    case "interactive":
      return {
        containerClass: "bg-white border-accent-200 border-l-accent-500",
        iconClass: "text-accent-500",
        titleClass: "text-ink-900",
        messageClass: "text-ink-600",
        buttonClass: "bg-accent-500 hover:bg-accent-600 focus:ring-accent-500",
        icon: CheckCircleIcon,
        focusRing: "focus:ring-accent-500/30",
      };
    case "info":
    default:
      return {
        containerClass: "bg-white border-ink-100 border-l-ink-500",
        iconClass: "text-ink-500",
        titleClass: "text-ink-900",
        messageClass: "text-ink-600",
        icon: InformationCircleIcon,
        focusRing: "focus:ring-ink-500/30",
      };
  }
};

const Toast = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const styles = getToastStyles(toast.type);
  const IconComponent = styles.icon;

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleRemove = () => {
    setIsExiting(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 280);
  };

  const animationClasses = classNames(
    "transition-all duration-280 ease-editorial transform",
    isVisible && !isExiting
      ? "translate-x-0 opacity-100 scale-100"
      : "translate-x-full opacity-0 scale-95"
  );

  return (
    <div
      className={classNames(
        "relative max-w-sm w-full rounded-card border border-l-4 p-4 shadow-lift mb-3",
        styles.containerClass,
        animationClasses
      )}
    >
      <div className="flex">
        <div className="flex-shrink-0">
          <IconComponent
            aria-hidden="true"
            className={classNames("h-5 w-5", styles.iconClass)}
          />
        </div>
        <div className="ml-3 flex-1">
          <h3 className={classNames("text-sm font-medium", styles.titleClass)}>
            {toast.title}
          </h3>
          {toast.message && (
            <div className={classNames("mt-1 text-sm", styles.messageClass)}>
              <p>{toast.message}</p>
            </div>
          )}
          {toast.buttonText && toast.onAction && (
            <div className="mt-3">
              <button
                type="button"
                className={classNames(
                  "inline-flex items-center rounded-pill px-3.5 py-1.5 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2",
                  styles.buttonClass || "bg-accent-500 hover:bg-accent-600"
                )}
                onClick={() => {
                  toast.onAction();
                  handleRemove();
                }}
              >
                {toast.buttonText}
              </button>
            </div>
          )}
        </div>
        <div className="ml-4 flex flex-shrink-0">
          <button
            type="button"
            className={classNames(
              "inline-flex rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 p-1 hover:bg-ink-900/5",
              styles.focusRing
            )}
            onClick={handleRemove}
          >
            <span className="sr-only">Dismiss</span>
            <XMarkIcon
              className={classNames("h-4 w-4 text-ink-400")}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Toast;
